import { VStack, Button, useDisclosure } from '@chakra-ui/react';
import { Accordion } from 'Atoms';
import { Typography } from 'Tokens';
import { TargetIcon, TargetOffIcon } from 'Tokens/Icons/Data';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAggregatedMetricsData } from '../Metrics';
import { IconHelpCircle } from '@tabler/icons-react';
import { LearnMoreDrawer } from 'Molecules';
import {
  DREnums,
  FrameworkRefs,
  useAssessmentReportingUnits,
  useMaterialStandardId,
} from 'containers/Esrs';
import { useParams, useSearchParams } from 'react-router-dom';
import { Metrics } from '../Metrics/Metrics';
import Header from '../DRHeader/Header.tsx';
import { DrInfoHeader } from '../Metrics/Metrics/MetricsComponents/DrInfoHeader.tsx';
import { useCompanyType } from 'utils/hooks/General.hooks.ts';
import { useEsrsAssessmentProjectLeaderQuery } from 'models/index.ts';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { GroupOrderOptions } from '../Metrics/AggregatedMetrics/AggregatedMetricsUtils.tsx';
import { TaskFilterOptions } from '../Metrics/MetricsTable/MetricsTable.utils.tsx';

export const ActionDisclosureRequirement = ({
  setTaskFilter,
  setSelectedUserId,
  selectedUserId,
  taskFilter,
  groupOrOrder,
  selectedAdditionalTypes,
  setAdditionalTypesOptions,
}: {
  setMetricSidebarOpen: (val: boolean) => void;
  setTaskFilter: (val: TaskFilterOptions) => void;
  setSelectedUserId: (val: string) => void;
  taskFilter: TaskFilterOptions;
  selectedUserId?: string;
  groupOrOrder?: GroupOrderOptions;
  selectedAdditionalTypes?: string[];
  setAdditionalTypesOptions?: (
    s: {
      label: string;
      ref: string;
    }[]
  ) => void;
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    esrsAssessmentId = '',
    standardRef = '',
    framework = '',
    disclosureRequirementRef = '',
    reportingUnitId,
  } = useParams();
  const { companyAssessmentId, parentAssessmentId, parentEsrsAssessmentId } = useMaterialStandardId(
    standardRef,
    esrsAssessmentId
  );
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [accordionOpenIndex, setAccordionOpenIndex] = useState<number[]>([]);
  const urlTaskUserId = useMemo(() => searchParams.get('taskUserId'), [searchParams]);

  const { companyType } = useCompanyType();
  const isGroup = useMemo(() => companyType === 'group-company', [companyType]);

  const { reportingUnitAssessments } = useAssessmentReportingUnits(esrsAssessmentId);
  const companyLevelReportingUnitId = useMemo(
    () => reportingUnitAssessments.find((ru) => ru.isCompanyLevel)?.id ?? '',
    [reportingUnitAssessments]
  );

  const { data: assessmentData } = useEsrsAssessmentProjectLeaderQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });
  const projectLeader = useMemo(
    () => assessmentData?.esrsAssessment?.projectLeader ?? undefined,
    [assessmentData]
  );

  const { metrics: plannedActionMetrics } = useGetAggregatedMetricsData(
    false,
    companyAssessmentId,
    parentAssessmentId,
    false
  );
  const { metrics: notPlannedActionMetrics } = useGetAggregatedMetricsData(
    true,
    companyAssessmentId,
    parentAssessmentId,
    false
  );

  useEffect(() => {
    if (urlTaskUserId) {
      setAccordionOpenIndex([0, 1]);
      if (urlTaskUserId === user?.id) setTaskFilter(TaskFilterOptions.yourTasks);
      else {
        setTaskFilter(TaskFilterOptions.members);
        setSelectedUserId(urlTaskUserId);
      }
    }
  }, [urlTaskUserId, user]);

  const accordionItems = [
    {
      id: 'planned',
      label: 'Disclosure requirements when actions are planned',
      icon: <TargetIcon mt="4px" />,
      content: (
        <Metrics
          noFilter={false}
          customTaskFilter={taskFilter}
          customSelectedAdditionalTypes={selectedAdditionalTypes}
          customGroupOrOrder={groupOrOrder}
          customSelectedMemberId={selectedUserId}
          setCustomTaskFilter={setTaskFilter}
          setAdditionalTypesOptions={setAdditionalTypesOptions}
        />
      ),
      extra: (
        <Button
          variant="ghost"
          leftIcon={<IconHelpCircle size="16px" />}
          fontWeight={500}
          onClick={(e) => {
            e.preventDefault();
            onOpen();
          }}
        >
          Learn more
        </Button>
      ),
    },
    {
      id: 'notPlanned',
      label: (
        <Typography variant="h3" as={'p'}>
          Disclosure requirements when actions are
          <Typography variant="h3" as={'span'} color="text.critical">
            {' '}
            not planned
          </Typography>
        </Typography>
      ),
      icon: <TargetOffIcon color="border.critical.accent" mt="3px" />,
      content: (
        <Metrics
          withAssociation={true}
          noFilter={false}
          customTaskFilter={taskFilter}
          customSelectedAdditionalTypes={selectedAdditionalTypes}
          customGroupOrOrder={groupOrOrder}
          customSelectedMemberId={selectedUserId}
          setCustomTaskFilter={setTaskFilter}
          setAdditionalTypesOptions={setAdditionalTypesOptions}
        />
      ),
      extra: (
        <Button
          variant="ghost"
          leftIcon={<IconHelpCircle size="16px" />}
          fontWeight={500}
          onClick={(e) => {
            e.preventDefault();
            onOpen();
          }}
        >
          Learn more
        </Button>
      ),
    },
  ];

  const filteredAccordionItems = useMemo(() => {
    return accordionItems.filter((item) => {
      if (item.id === 'planned') {
        return (plannedActionMetrics ?? []).length > 0;
      } else if (item.id === 'notPlanned') {
        return (notPlannedActionMetrics ?? []).length > 0;
      }
    });
  }, [accordionItems, plannedActionMetrics, notPlannedActionMetrics]);

  useEffect(() => {
    if (taskFilter !== TaskFilterOptions.allTasks) {
      setAccordionOpenIndex([0, 1]);
    }
  }, [taskFilter]);

  return (
    <VStack alignItems="start" w="100%" spacing="24px">
      <Header
        showInfobox={framework === FrameworkRefs.esrs}
        infoboxDescription="ESRS requires to disclose on different data points based when you do or don't have actions for each topic. Please answer the relevant data points below."
      />

      <DrInfoHeader
        drReference={disclosureRequirementRef}
        drType={DREnums.action}
        materialStandardId={companyAssessmentId}
        framework={framework}
        isBusinessUnit={false}
        isGroup={isGroup}
        standardRef={standardRef}
        esrsAssessmentId={esrsAssessmentId}
        parentAssessmentId={parentAssessmentId}
        projectLeader={projectLeader}
        reportingUnitId={reportingUnitId}
        companyReportingUnitId={companyLevelReportingUnitId}
        requiredDrMetrics={[...plannedActionMetrics, ...notPlannedActionMetrics].map(
          (item) => item.reference
        )}
        parentEsrsAssessmentId={parentEsrsAssessmentId}
      />

      {framework === FrameworkRefs.esrs ? (
        <Accordion
          variant="solid"
          items={filteredAccordionItems}
          openIndex={accordionOpenIndex}
          setOpenIndex={setAccordionOpenIndex}
        />
      ) : (
        <>
          <Metrics setAdditionalTypesOptions={setAdditionalTypesOptions} />
        </>
      )}
      <LearnMoreDrawer
        isOpen={isOpen}
        onClose={onClose}
        header="Action plan Disclosure Requirements"
        description='<div><p>ESRS requires disclosure on different data points based on whether you have an action plan for this topic or not. Please answer the relevant data points in one of the groups on this page.</p>
        <p><br></p>
        <p><span style="font-weight:600;" data-token-index="1" class="notion-enable-hover">Disclosure requirements when actions are planned</span></p>
        <p>Provide answers in this group if actions are planned for one or more of the material sustainability matters.</p>
        <p><br></p>
        <p><span style="font-weight:600;" data-token-index="3" class="notion-enable-hover">Disclosure requirements when actions are not planned</span></p>
        <p>Provide answers in this group if actions are not planned for one or more of the material sustainability matters.</p></div>'
      />
    </VStack>
  );
};
